import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  attachmentSchema,
  workOrderImportPayloadSchema,
  workOrderImportResponseSchema,
} from './workOrderImport.contract.js';
export type { WorkOrderImportPayload } from './workOrderImport.contract.js';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

// Use the same payload schema as work-order import with validated event types
export const workOrderDirectionalBodySchema = z.object({
  payload: workOrderImportPayloadSchema,
});

export const workOrderDirectionalResponseSchema = workOrderImportResponseSchema;

export type WorkOrderDirectionalBody = z.infer<typeof workOrderDirectionalBodySchema>;
export type WorkOrderDirectionalResponse = z.infer<typeof workOrderDirectionalResponseSchema>;

// Outbound accepted accepts an array of job IDs
export const outboundAcceptedBodySchema = z.object({
  ids: z.array(z.number().int().positive()),
});

export const outboundAcceptedResponseSchema = z.object({
  results: z.array(
    z.object({
      id: z.number(),
      success: z.boolean(),
      message: z.string(),
    }),
  ),
});

export type OutboundAcceptedBody = z.infer<typeof outboundAcceptedBodySchema>;
export type OutboundAcceptedResponse = z.infer<typeof outboundAcceptedResponseSchema>;

// Outbound scheduled accepts a single job ID
export const outboundScheduledBodySchema = z.object({
  id: z.number().int().positive(),
});

export const outboundScheduledResponseSchema = z.object({
  result: z.object({
    id: z.number(),
    success: z.boolean(),
    message: z.string(),
  }),
});

export type OutboundScheduledBody = z.infer<typeof outboundScheduledBodySchema>;
export type OutboundScheduledResponse = z.infer<typeof outboundScheduledResponseSchema>;

// Outbound unscheduled accepts a single job ID
export const outboundUnscheduledBodySchema = z.object({
  id: z.number().int().positive(),
});

export const outboundUnscheduledResponseSchema = z.object({
  result: z.object({
    id: z.number(),
    success: z.boolean(),
    message: z.string(),
  }),
});

export type OutboundUnscheduledBody = z.infer<typeof outboundUnscheduledBodySchema>;
export type OutboundUnscheduledResponse = z.infer<typeof outboundUnscheduledResponseSchema>;

// Outbound engineer en route accepts a single job ID
export const outboundEngineerEnRouteBodySchema = z.object({
  id: z.number().int().positive(),
});

export const outboundEngineerEnRouteResponseSchema = z.object({
  result: z.object({
    id: z.number(),
    success: z.boolean(),
    message: z.string(),
  }),
});

export type OutboundEngineerEnRouteBody = z.infer<typeof outboundEngineerEnRouteBodySchema>;
export type OutboundEngineerEnRouteResponse = z.infer<typeof outboundEngineerEnRouteResponseSchema>;

// Outbound engineer on site accepts a single job ID
export const outboundEngineerOnSiteBodySchema = z.object({
  id: z.number().int().positive(),
});

export const outboundEngineerOnSiteResponseSchema = z.object({
  result: z.object({
    id: z.number(),
    success: z.boolean(),
    message: z.string(),
  }),
});

export type OutboundEngineerOnSiteBody = z.infer<typeof outboundEngineerOnSiteBodySchema>;
export type OutboundEngineerOnSiteResponse = z.infer<typeof outboundEngineerOnSiteResponseSchema>;

export const outboundAbortedBodySchema = z.object({
  id: z.number().int().positive(),
  notes: z
    .array(
      z.object({
        id: z.string().optional(),
        note: z.string(),
        createdBy: z.string(),
        createdDate: z.string(),
        category: z.string().optional(),
      }),
    )
    .min(1),
});

// Outbound duration challenge — supervisor/engineer challenges the duration window
export const outboundDurationChallengeBodySchema = z.object({
  id: z.number().int().positive(),
  schedule: z.object({
    duration: z.number().positive(),
    durationUnit: z.enum(['hours', 'days']),
    windowStartDate: z.string().optional(),
    windowEndDate: z.string().optional(),
  }),
  notes: z
    .array(
      z.object({
        note: z.string(),
        createdBy: z.string(),
        createdDate: z.string(),
      }),
    )
    .min(1),
});

export const outboundAbortedResponseSchema = z.object({
  result: z.object({
    id: z.number(),
    success: z.boolean(),
    message: z.string(),
  }),
});

export const outboundDurationChallengeResponseSchema = z.object({
  result: z.object({
    id: z.number(),
    success: z.boolean(),
    message: z.string(),
  }),
});

export type OutboundDurationChallengeBody = z.infer<typeof outboundDurationChallengeBodySchema>;
export type OutboundDurationChallengeResponse = z.infer<
  typeof outboundDurationChallengeResponseSchema
>;

export type OutboundAbortedBody = z.infer<typeof outboundAbortedBodySchema>;
export type OutboundAbortedResponse = z.infer<typeof outboundAbortedResponseSchema>;

export const outboundCancelFromAbortBodySchema = z.object({
  id: z.number().int().positive(),
});

export const outboundCancelFromAbortResponseSchema = z.object({
  result: z.string(),
});

export type OutboundCancelFromAbortBody = z.infer<typeof outboundCancelFromAbortBodySchema>;
export type OutboundCancelFromAbortResponse = z.infer<typeof outboundCancelFromAbortResponseSchema>;

export const inboundCanceledBodySchema = z.object({
  meta: z.object({
    messageId: z.string().uuid(),
    eventType: z.string(),
    sentAt: z.string(),
    source: z.string(),
    version: z.string().optional(),
  }),
  workOrder: z.object({
    id: z.string(),
    notes: z
      .array(
        z.object({
          id: z.string().optional(),
          note: z.string(),
          createdBy: z.string().optional(),
          createdDate: z.string().optional(),
        }),
      )
      .optional(),
  }),
});

export type InboundCanceledBody = z.infer<typeof inboundCanceledBodySchema>;

export const outboundCancelBodySchema = z.object({
  id: z.number().int().positive(),
  notes: z
    .array(
      z.object({
        note: z.string(),
        createdBy: z.string().optional(),
        createdDate: z.string().optional(),
      }),
    )
    .optional(),
});

export const outboundCancelResponseSchema = z.object({
  result: z.object({ id: z.number(), success: z.boolean(), message: z.string() }),
});

export type OutboundCancelBody = z.infer<typeof outboundCancelBodySchema>;
export type OutboundCancelResponse = z.infer<typeof outboundCancelResponseSchema>;

export const outboundReplanFromAbortBodySchema = z.object({
  id: z.number().int().positive(),
});

export const outboundReplanFromAbortResponseSchema = z.object({
  result: z.string(),
});

export type OutboundReplanFromAbortBody = z.infer<typeof outboundReplanFromAbortBodySchema>;
export type OutboundReplanFromAbortResponse = z.infer<typeof outboundReplanFromAbortResponseSchema>;

export const outboundEngineeringCompleteBodySchema = z.object({
  id: z.number().int().positive(),
  notes: z
    .array(
      z.object({
        id: z.string().optional(),
        note: z.string(),
        createdBy: z.string(),
        createdDate: z.string(),
      }),
    )
    .optional(),
});

export const outboundEngineeringCompleteResponseSchema = z.object({
  result: z.object({
    id: z.number(),
    success: z.boolean(),
    message: z.string(),
  }),
});

export type OutboundEngineeringCompleteBody = z.infer<typeof outboundEngineeringCompleteBodySchema>;
export type OutboundEngineeringCompleteResponse = z.infer<
  typeof outboundEngineeringCompleteResponseSchema
>;

export const outboundDfeSubmittedBodySchema = z.object({
  id: z.number().int().positive(),
  dfe: z.object({
    workPerformed: z.string().min(1),
    reinstatementType: z.enum(['permanent', 'temporary', 'none']).optional(),
    materialsUsed: z
      .array(
        z.object({
          code: z.string(),
          description: z.string(),
          quantity: z.number(),
          unit: z.string(),
        }),
      )
      .optional(),
    safetyObservations: z.string().optional(),
    customerSignature: z.string().optional(),
    attachments: z
      .array(
        z.object({
          type: z.string(),
          filename: z.string(),
        }),
      )
      .optional(),
  }),
  notes: z
    .array(
      z.object({
        note: z.string(),
        createdBy: z.string().optional(),
        createdDate: z.string().optional(),
      }),
    )
    .optional(),
});

export const outboundDfeSubmittedResponseSchema = z.object({
  result: z.object({
    id: z.number(),
    success: z.boolean(),
    message: z.string(),
  }),
});

export type OutboundDfeSubmittedBody = z.infer<typeof outboundDfeSubmittedBodySchema>;
export type OutboundDfeSubmittedResponse = z.infer<typeof outboundDfeSubmittedResponseSchema>;

export const outboundCompletedBodySchema = z.object({
  id: z.number().int().positive(),
  sapClosureCode: z.string().optional(),
  invoice: z
    .object({
      reference: z.string().optional(),
      amount: z.number().optional(),
    })
    .optional(),
  notes: z
    .array(
      z.object({
        id: z.string().optional(),
        note: z.string(),
        createdBy: z.string(),
        createdDate: z.string(),
      }),
    )
    .optional(),
});

export const outboundCompletedResponseSchema = z.object({
  result: z.object({
    id: z.number(),
    success: z.boolean(),
    message: z.string(),
  }),
});

export type OutboundCompletedBody = z.infer<typeof outboundCompletedBodySchema>;
export type OutboundCompletedResponse = z.infer<typeof outboundCompletedResponseSchema>;

// Inbound duration challenge approved/rejected — Core responds to a challenge
export const inboundDurationChallengeApprovedBodySchema = z.object({
  meta: z.object({
    messageId: z.string().uuid(),
    eventType: z.string(),
    sentAt: z.string(),
    source: z.string(),
    version: z.string().optional(),
    deliveryPartnerCode: z.string().optional(),
  }),
  outcome: z.enum(['approved', 'rejected']),
  workOrder: z.object({
    id: z.string(),
    notes: z
      .array(
        z.object({
          id: z.string().optional(),
          note: z.string(),
          createdBy: z.string(),
          createdDate: z.string(),
        }),
      )
      .optional(),
  }),
  schedule: z
    .object({
      windowStartDate: z.string().optional(),
      windowEndDate: z.string().optional(),
      duration: z.number().optional(),
      durationUnit: z.string().optional(),
    })
    .optional(),
});

export type InboundDurationChallengeApprovedBody = z.infer<
  typeof inboundDurationChallengeApprovedBodySchema
>;

// Inbound DFE approved — Core confirms submitted DFE has been approved
const inboundDfeApprovedPayloadSchema = z.object({
  meta: z.object({
    messageId: z.string().uuid(),
    eventType: z.string(),
    sentAt: z.string(),
    source: z.string(),
    version: z.string().optional(),
  }),
  workOrder: z.object({
    id: z.string(),
    notes: z
      .array(
        z.object({
          id: z.string().optional(),
          note: z.string(),
          createdBy: z.string().optional(),
          createdDate: z.string().optional(),
        }),
      )
      .optional(),
  }),
  dfe: z
    .object({
      approvedBy: z.string().optional(),
      approvedAt: z.string().optional(),
    })
    .optional(),
});

export const inboundDfeApprovedBodySchema = inboundDfeApprovedPayloadSchema;

export type InboundDfeApprovedBody = z.infer<typeof inboundDfeApprovedBodySchema>;

/**
 * MIR (More Info Requested / More Info Received) payload schema.
 * A lightweight alternative to the full work-order import payload used
 * specifically for more_info_requested and more_info_received events.
 */
const mirMetaSchema = z.object({
  messageId: z.string().uuid(),
  eventType: z.string(),
  sentAt: z.string().datetime(),
  source: z.string(),
  version: z.string().optional(),
  igniteEventId: z.string().optional(),
  userId: z.union([z.string(), z.number()]).optional(),
});

const mirNoteSchema = z.object({
  id: z.string().optional(),
  note: z.string().max(2000),
  createdBy: z.string().max(150),
  createdDate: z.string().datetime(),
  category: z.string().nullable().optional(),
});

export const workOrderMirPayloadSchema = z.object({
  meta: mirMetaSchema,
  workOrder: z.object({
    id: z.string(),
    notes: z.array(mirNoteSchema).optional(),
    attachments: z.array(attachmentSchema).optional(),
  }),
});

export const workOrderMirBodySchema = workOrderMirPayloadSchema;

export type WorkOrderMirPayload = z.infer<typeof workOrderMirPayloadSchema>;
export type WorkOrderMirBody = z.infer<typeof workOrderMirBodySchema>;

/**
 * Shared per-job item shape used by both /outbound/rejected and
 * /outbound/more-info-requested. `externalId` is optional because the backend
 * already reads it from the job row when validating — the client doesn't have
 * it available in the jobs list response, and forcing a round-trip just to
 * surface it would be wasteful.
 */
const outboundJobNoteItemSchema = z.object({
  id: z.number().int().positive(),
  externalId: z.string(),
  notes: z
    .array(
      z.object({
        id: z.string().optional(),
        note: z.string(),
        createdBy: z.string(),
        createdDate: z.string(),
        category: z.string().optional(),
      }),
    )
    .min(1),
  attachments: z.array(attachmentSchema).nullable().optional(),
});

export const outboundRejectedBodySchema = z.object({
  rejections: z.array(outboundJobNoteItemSchema).min(1),
});

const outboundResultsResponseSchema = z.object({
  results: z.array(
    z.object({
      id: z.number(),
      success: z.boolean(),
      message: z.string(),
    }),
  ),
});

export const outboundRejectedResponseSchema = outboundResultsResponseSchema;

export type OutboundRejectedBody = z.infer<typeof outboundRejectedBodySchema>;
export type OutboundRejectedResponse = z.infer<typeof outboundRejectedResponseSchema>;

/**
 * Outbound more-info-requested — same shape as rejected. The bulk UI collects
 * one free-text question per job (mapped to a single note entry) and ships
 * them together so the backend can dispatch per-job events to CORE.
 */
export const outboundMoreInfoRequestedBodySchema = z.object({
  requests: z.array(outboundJobNoteItemSchema).min(1),
});

export const outboundMoreInfoRequestedResponseSchema = outboundResultsResponseSchema;

export type OutboundMoreInfoRequestedBody = z.infer<typeof outboundMoreInfoRequestedBodySchema>;
export type OutboundMoreInfoRequestedResponse = z.infer<
  typeof outboundMoreInfoRequestedResponseSchema
>;

/**
 * Outbound more-info-received — single-job reply to an inbound info request.
 * Simpler than the MIR inbound schema: just the work order ID, notes, and
 * optional attachments. No meta envelope needed for outbound.
 */
export const outboundMoreInfoReceivedBodySchema = z.object({
  jobWorkOrder: outboundJobNoteItemSchema,
});

export const outboundMoreInfoReceivedResponseSchema = outboundResultsResponseSchema;

export type OutboundMoreInfoReceivedBody = z.infer<typeof outboundMoreInfoReceivedBodySchema>;
export type OutboundMoreInfoReceivedResponse = z.infer<
  typeof outboundMoreInfoReceivedResponseSchema
>;

// Inbound work_order.validated — Ignite Core confirms validation approved
export const inboundValidatedBodySchema = z.object({
  meta: z.object({
    messageId: z.string().uuid(),
    eventType: z.string(),
    sentAt: z.string(),
    source: z.string(),
    version: z.string().optional(),
  }),
  workOrder: z.object({
    id: z.string(),
  }),
});

export type InboundValidatedBody = z.infer<typeof inboundValidatedBodySchema>;

// Inbound work_order.update — free-form update pushed by Ignite Core at any time
export const inboundWorkOrderUpdateBodySchema = z.object({
  meta: z.object({
    messageId: z.string().uuid(),
    eventType: z.string(),
    sentAt: z.string(),
    source: z.string(),
    version: z.string().optional(),
  }),
  workOrder: z.object({
    id: z.string(),
    additionalData: z.record(z.unknown()).optional(),
  }),
});

export type InboundWorkOrderUpdateBody = z.infer<typeof inboundWorkOrderUpdateBodySchema>;

/**
 * Outbound validation-requested — submit completed engineering work with DFE for validation.
 * Sent after work_order.engineering_complete when DFE is required.
 */
export const outboundValidationRequestedBodySchema = z.object({
  id: z.number().int().positive(),
  sapClosureCode: z.string().optional(),
  notes: z
    .array(
      z.object({
        note: z.string(),
        createdBy: z.string().optional(),
        createdDate: z.string().optional(),
      }),
    )
    .optional(),
  invoice: z
    .object({
      reference: z.string().optional(),
      amount: z.number().optional(),
    })
    .optional(),
  dfe: z
    .object({
      submittedBy: z.string(),
      submittedAt: z.string(),
      workPerformed: z.string(),
      onSiteArrival: z.string().optional(),
      onSiteDeparture: z.string().optional(),
      reinstatementType: z.enum(['permanent', 'temporary', 'none']).optional(),
      materialsUsed: z
        .array(
          z.object({
            code: z.string(),
            description: z.string(),
            quantity: z.number(),
            unit: z.string(),
          }),
        )
        .optional(),
      safetyObservations: z.string().optional(),
      customerSignature: z.string().optional(),
      attachments: z.array(z.object({ type: z.string(), filename: z.string() })).optional(),
    })
    .optional(),
});

export const outboundValidationRequestedResponseSchema = z.object({
  result: z.object({
    id: z.number(),
    success: z.boolean(),
    message: z.string(),
  }),
});

export type OutboundValidationRequestedBody = z.infer<typeof outboundValidationRequestedBodySchema>;
export type OutboundValidationRequestedResponse = z.infer<
  typeof outboundValidationRequestedResponseSchema
>;

// Outbound allocated — assign engineers and schedule data to a work order
export const outboundAllocatedBodySchema = z.object({
  id: z.number().int().positive(),
  schedule: z.object({
    duration: z.number().nullable().optional(),
    durationUnit: z.string().default('hours'),
    windowEndDate: z.string().nullable().optional(),
    windowStartDate: z.string().nullable().optional(),
  }),
  assignedEngineers: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      role: z.string(),
    }),
  ),
});

export const outboundAllocatedResponseSchema = z.object({
  result: z.object({
    id: z.number(),
    success: z.boolean(),
    message: z.string(),
  }),
});

export type OutboundAllocatedBody = z.infer<typeof outboundAllocatedBodySchema>;
export type OutboundAllocatedResponse = z.infer<typeof outboundAllocatedResponseSchema>;

type RouteConfig = {
  path: string;
  summary: string;
  description: string;
};

const createRoute = ({ path, summary, description }: RouteConfig) => ({
  method: 'POST' as const,
  path,
  body: workOrderDirectionalBodySchema,
  responses: {
    200: workOrderDirectionalResponseSchema,
    500: errorResponseSchema,
  },
  summary,
  description,
});

export const workOrderDirectionalContract = c.router({
  inboundNew: {
    method: 'POST' as const,
    path: '/inbound/new',
    body: workOrderImportPayloadSchema,
    responses: {
      200: workOrderImportResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Inbound: new work order',
    description: 'Inbound endpoint for work_order.new — accepts the payload directly (no wrapper)',
  },
  inboundDurationChallengeApproved: {
    method: 'POST' as const,
    path: '/inbound/duration-challenge-approved',
    body: inboundDurationChallengeApprovedBodySchema,
    responses: {
      200: workOrderDirectionalResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Inbound: duration challenge approved',
    description:
      'Inbound endpoint for work_order.duration_challenge_approved — receives approval or rejection of a duration challenge from Ignite Core. On approval: updates schedule and transitions to Accepted/Duration Challenge Approved. On rejection: stays On Hold/Duration Challenged Rejected.',
  },
  inboundDfeMoreInfoRequested: createRoute({
    path: '/inbound/dfe-more-info-requested',
    summary: 'Inbound: DFE more info requested',
    description: 'Template inbound endpoint for work_order.dfe_more_info_requested',
  }),
  inboundDfeApproved: {
    method: 'POST' as const,
    path: '/inbound/dfe-approved',
    body: inboundDfeApprovedBodySchema,
    responses: {
      200: workOrderDirectionalResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Inbound: DFE approved',
    description:
      'Inbound endpoint for work_order.dfe_approved — updates work order status to Validated and records optional dfe approval metadata.',
  },
  inboundMoreInfoRequested: {
    method: 'POST' as const,
    path: '/inbound/more-info-requested',
    body: workOrderMirBodySchema,
    responses: {
      200: workOrderDirectionalResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Inbound: more info requested',
    description:
      'Inbound endpoint for work_order.more_info_requested — accepts lightweight MIR payload with notes and optional attachments',
  },
  inboundMoreInfoReceived: {
    method: 'POST' as const,
    path: '/inbound/more-info-received',
    body: workOrderMirBodySchema,
    responses: {
      200: workOrderDirectionalResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Inbound: more info received',
    description:
      'Inbound endpoint for work_order.more_info_received — accepts lightweight MIR payload with notes and optional attachments',
  },
  inboundValidated: {
    method: 'POST' as const,
    path: '/inbound/validated',
    body: inboundValidatedBodySchema,
    responses: {
      200: workOrderDirectionalResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Inbound: validated',
    description:
      'Inbound endpoint for work_order.validated — Ignite Core confirms validation approved. Validates job is In Progress with Validation Requested work order status, then transitions both to Completed.',
  },
  inboundWorkOrderUpdate: {
    method: 'POST' as const,
    path: '/inbound/work-order-update',
    body: inboundWorkOrderUpdateBodySchema,
    responses: {
      200: workOrderDirectionalResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Inbound: work order update',
    description:
      'Inbound endpoint for work_order.update — free-form update pushed by Ignite Core at any time. Always logged to communication_log. Only stored in work_order_update table if the work order exists in Works Lite.',
  },
  outboundRejected: {
    method: 'POST' as const,
    path: '/outbound/rejected',
    body: outboundRejectedBodySchema,
    responses: {
      200: outboundRejectedResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: rejected',
    description:
      'Reject one or more work orders by job ID — for each: validates job is in Not Started/New statuses and has an external_id, sends work_order.rejected event to CORE with rejection notes, then updates statuses to Rejected/Cancelled. Returns per-ID success/failure results.',
  },
  outboundAccepted: {
    method: 'POST' as const,
    path: '/outbound/accepted',
    body: outboundAcceptedBodySchema,
    responses: {
      200: outboundAcceptedResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: accepted',
    description:
      'Accept one or more work orders by job IDs — for each ID: validates job is in Not Started/New statuses and has an external_id, sends work_order.accepted event to CORE, then updates statuses to Accepted/Ready for Planning. Returns per-ID success/failure results.',
  },
  outboundAllocated: {
    method: 'POST' as const,
    path: '/outbound/allocated',
    body: outboundAllocatedBodySchema,
    responses: {
      200: outboundAllocatedResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: allocated',
    description:
      'Allocate a work order by job ID — sends work_order.allocated event to CORE with schedule and assigned engineers data, then updates job.assigned_to with the first engineer ID.',
  },
  outboundDurationChallenge: {
    method: 'POST' as const,
    path: '/outbound/duration-challenge',
    body: outboundDurationChallengeBodySchema,
    responses: {
      200: outboundDurationChallengeResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: duration challenge',
    description:
      'Challenge the duration/schedule window on a work order by job ID — validates job is in Accepted/Ready for Planning statuses and has an external_id, sends work_order.duration_challenge event to CORE with proposed schedule and notes, then updates statuses to On Hold/Duration Challenged.',
  },
  outboundScheduled: {
    method: 'POST' as const,
    path: '/outbound/scheduled',
    body: outboundScheduledBodySchema,
    responses: {
      200: outboundScheduledResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: scheduled',
    description:
      'Schedule a work order by job ID — validates job is in Accepted/Ready for Planning statuses and has an external_id, sends work_order.scheduled event to CORE with schedule, engineer and traffic management data, then updates statuses to Planned/Planned.',
  },
  outboundUnscheduled: {
    method: 'POST' as const,
    path: '/outbound/unscheduled',
    body: outboundUnscheduledBodySchema,
    responses: {
      200: outboundUnscheduledResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: unscheduled',
    description:
      'Unschedule a work order by job ID — validates job is in Planned/Planned statuses and has an external_id, sends work_order.unscheduled event to CORE, then reverts statuses to Accepted/Ready for Planning.',
  },
  outboundEngineerEnRoute: {
    method: 'POST' as const,
    path: '/outbound/engineer-en-route',
    body: outboundEngineerEnRouteBodySchema,
    responses: {
      200: outboundEngineerEnRouteResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: engineer en route',
    description:
      'Send engineer en route notification for a work order by job ID — validates job is in Planned/Planned statuses and has an assigned engineer, sends work_order.engineer_en_route event to CORE, then updates statuses to In Progress/En Route.',
  },
  outboundEngineerOnSite: {
    method: 'POST' as const,
    path: '/outbound/engineer-on-site',
    body: outboundEngineerOnSiteBodySchema,
    responses: {
      200: outboundEngineerOnSiteResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: engineer on site',
    description:
      'Send engineer on site notification for a work order by job ID — validates job is in In Progress/En Route statuses and has an assigned engineer, sends work_order.engineer_on_site event to CORE, then updates work order status to On Site (job status stays In Progress).',
  },
  outboundAborted: {
    method: 'POST' as const,
    path: '/outbound/aborted',
    body: outboundAbortedBodySchema,
    responses: {
      200: outboundAbortedResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: aborted',
    description:
      'Abort a work order by job ID — validates job is in Accepted, Planned, or In Progress status and has an external_id, sends work_order.aborted event to CORE with abort reason notes, then updates statuses to Aborted/Aborted.',
  },
  outboundEngineeringComplete: {
    method: 'POST' as const,
    path: '/outbound/engineering-complete',
    body: outboundEngineeringCompleteBodySchema,
    responses: {
      200: outboundEngineeringCompleteResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: engineering complete',
    description:
      'Mark engineering complete for a work order by job ID — validates job is In Progress with On Site work order status and has an assigned engineer, sends work_order.engineering_complete event to CORE with engineer completion data, then updates work order status to Engineering Complete (job status stays In Progress).',
  },
  outboundDfeSubmitted: {
    method: 'POST' as const,
    path: '/outbound/dfe-submitted',
    body: outboundDfeSubmittedBodySchema,
    responses: {
      200: outboundDfeSubmittedResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: DFE submitted',
    description:
      'Submit a Departure From Estimate for a work order by job ID — validates job is In Progress with Engineering Completed work order status, sends work_order.dfe_submitted event to CORE with full dfe object, then updates work order status to DFE Requested (job status stays In Progress).',
  },
  outboundDfeWithdrawn: createRoute({
    path: '/outbound/dfe-withdrawn',
    summary: 'Outbound: DFE withdrawn',
    description: 'Template outbound endpoint for work_order.dfe_withdrawn',
  }),
  outboundDfeResubmitted: createRoute({
    path: '/outbound/dfe-resubmitted',
    summary: 'Outbound: DFE resubmitted',
    description: 'Template outbound endpoint for work_order.dfe_resubmitted',
  }),
  outboundCompleted: {
    method: 'POST' as const,
    path: '/outbound/completed',
    body: outboundCompletedBodySchema,
    responses: {
      200: outboundCompletedResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: completed',
    description:
      'Mark a work order as completed by job ID — validates job is In Progress with work order status DFE Approved or Engineering Complete, sends work_order.completed event to CORE, then updates both statuses to Complete/Complete.',
  },
  outboundMoreInfoRequested: {
    method: 'POST' as const,
    path: '/outbound/more-info-requested',
    body: outboundMoreInfoRequestedBodySchema,
    responses: {
      200: outboundMoreInfoRequestedResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: more info requested',
    description:
      'Request more information on one or more work orders by job ID — for each: validates job is in Not Started/New statuses and has an external_id, sends work_order.more_info_requested event to CORE with per-job notes, then updates statuses. Returns per-ID success/failure results.',
  },
  outboundMoreInfoReceived: {
    method: 'POST' as const,
    path: '/outbound/more-info-received',
    body: outboundMoreInfoReceivedBodySchema,
    responses: {
      200: outboundMoreInfoReceivedResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: more info received',
    description:
      'Outbound endpoint for work_order.more_info_received — reply to an inbound info request with notes and optional attachments',
  },
  outboundValidationRequested: {
    method: 'POST' as const,
    path: '/outbound/validation-requested',
    body: outboundValidationRequestedBodySchema,
    responses: {
      200: outboundValidationRequestedResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: validation requested',
    description:
      'Submit completed engineering work with DFE for validation — validates job is In Progress with Engineering Completed work order status, sends work_order.validation_requested event to CORE with optional invoice and DFE data, then updates work order status to Validation Requested.',
  },
  outboundCancelFromAbort: {
    method: 'POST' as const,
    path: '/outbound/cancel-from-abort',
    body: outboundCancelFromAbortBodySchema,
    responses: {
      200: outboundCancelFromAbortResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: cancel from abort',
    description:
      'Cancel a work order that is in Abort/Aborted state — sends work_order.cancelled event to CORE, then updates JS to Cancelled (WOS stays Aborted). Saves communication log entry.',
  },
  inboundCanceled: {
    method: 'POST' as const,
    path: '/inbound/canceled',
    body: inboundCanceledBodySchema,
    responses: {
      200: workOrderDirectionalResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Inbound: canceled',
    description:
      'Inbound work_order.cancelled from CORE — updates job to Cancelled/Cancelled for allowed status combinations. Planned/Planned is explicitly rejected.',
  },
  outboundCancel: {
    method: 'POST' as const,
    path: '/outbound/cancel',
    body: outboundCancelBodySchema,
    responses: {
      200: outboundCancelResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: cancel',
    description:
      'Cancel a work order from LITE — validates allowed status combinations, sends work_order.cancelled event to CORE, updates DB to Cancelled/Cancelled.',
  },
  outboundReplanFromAbort: {
    method: 'POST' as const,
    path: '/outbound/replan-from-abort',
    body: outboundReplanFromAbortBodySchema,
    responses: {
      200: outboundReplanFromAbortResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: replan from abort',
    description:
      'Replan a work order that is in Abort/Aborted state — local-only update, no CORE call. Updates WOS to Ready for Planning (JS stays Abort). No communication log entry.',
  },
});
